import { useCallback } from 'react';

import { useMapFeaturesContext } from 'shared/context/MapLayersCenterContext';
import { useMapConfigurationSelector } from 'shared/store/clientSide/slices/mapConfiguration';
import { Cluster, RBushItem } from 'shared/types/Map';
import { convertClusterToRBushItem } from 'shared/utils/map/convertClusterToRBushItem';
import { rbushEquals } from 'shared/utils/map/rbushEquals';
import { getTilesToUpdate } from 'shared/utils/map/tiles';

type UseUpdateChangedClustersResult = (...clusters: (Cluster | undefined)[]) => void;

/**
 * Утильный хук:
 * - получает кластеры, которые нужно обновить
 * - обновляет rbush-айтемы, которые соответствуют кластерам, в дереве rbush (delete + insert)
 * - обновляет нужные тайлы слоя пинов
 * - обновляет балуны пинов
 */
export function useUpdateChangedClusters(): UseUpdateChangedClustersResult {
  const { zoom, bounds } = useMapConfigurationSelector();
  const { pinsLayer, balloonManager, rbush } = useMapFeaturesContext();

  return useCallback(
    (...clusters) => {
      const rbushItems = clusters.reduce<RBushItem[]>((acc, cluster) => {
        if (cluster) {
          const rbushItem = convertClusterToRBushItem(cluster);

          rbush.remove(rbushItem, rbushEquals);
          rbush.insert(rbushItem);

          /**
           * updateBalloonOptions - чтобы обновился баллун (его отображение) с учетом новых данных
           * updateBalloonProperties - чтобы обновились данные самого баллуна и были актуальными там, где мы их
           * затем используем (например при клике на пин мы отправляем аналитику, там используется содержимое поля
           * properties баллуна)
           */
          balloonManager.updateBalloonOptions(cluster);
          balloonManager.updateBalloonProperties(cluster);

          acc.push(rbushItem);

          return acc;
        }

        return acc;
      }, []);

      const tilesToUpdate = getTilesToUpdate({ bounds, rbushItems, zoom });

      pinsLayer.layer.update(tilesToUpdate);
    },
    [balloonManager, bounds, pinsLayer.layer, rbush, zoom],
  );
}
